import { useCallback, useEffect } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  withTranslation,
  WithTranslation,
  useTranslation,
} from 'react-i18next';

import { applyTheme } from '../../utils';
import { PatientRouteParams } from '../../scenes/router';

import * as api from '../../api';
import * as Cache from '../../utils/cache';
import * as rootReducer from '../../reducers/root';
import * as store from '../../reducers/store';

import Loader from '../Loader';
import { sendEventMessage, Message } from '../../utils/native';
import { changeGlobalMomentLocale } from '../../i18n';
import {
  FormTypes,
  getFormEndPath,
  getMaybeDocumentSignerFormField,
} from '../../utils/form';
import { FormSets } from '../../reducers/store';
import {
  allCompleted,
  getFormIdFromShortOrFullFormId,
  getFormsFromFormSets,
} from '../../utils/formSet';

type PrevisitFormSetWithForms = api.PrevisitFormSet.PrevisitFormSetWithForms;

interface MakePatientFetcherProps
  extends StateProps,
    DispatchProps,
    WithTranslation,
    React.PropsWithChildren {
  multi?: boolean;
}

interface StateProps {
  authenticationCode: string | null;
  preferredLanguage: store.Language;
  formSets: FormSets;
  selectedFormSetId: string | null;
  selectedFormId: string | null;
}

interface DispatchProps {
  setSelectedFormSetId: (selectedFormSetId: string | null) => void;
  setSelectedFormId: (selectedFormId: string | null) => void;
  setFormSets: (
    formSets: api.PrevisitFormSet.PrevisitFromSetWithPatientData[],
  ) => void;
  setMultiFormSet: (multi: boolean) => void;
  setAuthenticationCode: (authenticationCode: string) => void;
  setLoggedInPatient: (patient: api.Patient.Patient) => void;
  setDocuments: (
    formSetId: string,
    document: api.PrevisitDocument.DocumentWithTemplate[],
  ) => void;
  setFeedback: (feedback: store.PatientFeedback | null) => void;
  setTheme: (theme: api.PracticeTheme.ThemeResponse) => void;
  setCoordinates: (coordinates: api.Places.Geometry | undefined) => void;
}

// render-props component w/ router params to take care of fetching
const MakePatientFetcher: React.FC<MakePatientFetcherProps> = ({
  authenticationCode: storeAuthCode,
  setAuthenticationCode,
  setLoggedInPatient,
  selectedFormSetId,
  selectedFormId,
  setDocuments,
  setFeedback,
  setSelectedFormId,
  setSelectedFormSetId,
  setMultiFormSet,
  setFormSets,
  setTheme,
  setCoordinates,
  formSets,
  preferredLanguage,
  children,
  multi,
}) => {
  const {
    authenticationCode: paramAuthCode,
    formSetId: pathFormSetId,
    formId,
  } = useParams<PatientRouteParams>();
  const history = useHistory();
  const { i18n } = useTranslation();
  const currentFormId = getFormIdFromShortOrFullFormId({
    formSets,
    formSetId: selectedFormSetId,
    shortOrFullFormId: formId,
  });
  // If no path form set id,we do not need to wait selected form set id to be set
  const formSetIdIdentified =
    !pathFormSetId || (selectedFormSetId && selectedFormId);

  const logoutAndNavigate = useCallback(async () => {
    await api.client.Previsit.logout();
    Cache.clearCache();
    if ((window as any).webkit) {
      sendEventMessage(Message.Logout);
    } else {
      history.push({
        pathname: `/login/${paramAuthCode}`,
        search: '',
      });
    }
  }, [paramAuthCode, history]);

  useEffect(() => {
    if (storeAuthCode === paramAuthCode) {
      // don't re-fetch if done once
      return;
    }
    if (preferredLanguage && preferredLanguage !== i18n.language) {
      i18n.changeLanguage(preferredLanguage);
      changeGlobalMomentLocale(preferredLanguage);
    }

    function navigateToLogin() {
      Cache.clearCache();
      history.push({
        pathname: `/login/${paramAuthCode}`,
        search: '',
      });
    }

    async function errorHandler<T>(fcn: Promise<T>): Promise<T | undefined> {
      try {
        return await fcn;
      } catch (e) {
        if (e.status === 401) {
          await logoutAndNavigate();
        } else {
          navigateToLogin();
        }
      }
    }

    async function maybeLoadDocuments(
      previsitFormSet: PrevisitFormSetWithForms,
    ) {
      const existingDocumentSigner = getMaybeDocumentSignerFormField(
        previsitFormSet.forms,
      );
      if (!existingDocumentSigner) return;
      const documents =
        await api.client.PrevisitDocumentTemplate.findAllByFormSetAndField(
          existingDocumentSigner.previsitFormSetId,
          existingDocumentSigner.id,
        );
      setDocuments(previsitFormSet.id, documents);
    }

    async function doEffect() {
      const authenticated = await errorHandler(
        api.client.Previsit.isAuthenticated(),
      );
      if (!authenticated) {
        setFeedback(null);
        navigateToLogin();
        return;
      }

      const [patientData, previsitFormSetsResponse] = await Promise.all([
        errorHandler(Cache.fetchPatientData(paramAuthCode)),
        errorHandler(
          api.client.Previsit.fetchFormSets(
            paramAuthCode,
            preferredLanguage,
            multi,
            true,
          ),
        ),
      ] as const);
      if (!patientData || !previsitFormSetsResponse?.formSets?.length) {
        logoutAndNavigate();
        return;
      }
      const { formSets } = previsitFormSetsResponse;
      const parentFormSet = formSets[0].previsitFormSet;
      setTheme(parentFormSet.theme);
      setMultiFormSet(!!multi);
      applyTheme(parentFormSet.theme);
      setAuthenticationCode(paramAuthCode);
      setLoggedInPatient(patientData.patient);
      setFormSets(formSets);
      setCoordinates(
        patientData.patient?.physicalAddress ??
          patientData.patient?.mailingAddress,
      );
      await Promise.all(
        formSets.map((formSet) => maybeLoadDocuments(formSet.previsitFormSet)),
      );
    }
    doEffect();
  }, [
    setAuthenticationCode,
    setDocuments,
    setFeedback,
    setFormSets,
    setLoggedInPatient,
    setMultiFormSet,
    setTheme,
    setCoordinates,
    multi,
    history,
    paramAuthCode,
    formSets,
    storeAuthCode,
    logoutAndNavigate,
    preferredLanguage,
    i18n,
  ]);

  useEffect(() => {
    const completed = allCompleted(formSets);
    if (completed) {
      const forms = Object.values(
        getFormsFromFormSets(formSets, FormTypes.All),
      );
      const incompleteForms = forms.filter(
        (form) => form.status !== api.PrevisitForm.Status.Completed,
      );
      const incompleteFormIds = incompleteForms.map((form) => form.id);

      const isIncompleteFormPage = incompleteFormIds.some((formId: string) =>
        history.location.pathname.includes(`/${formId}`),
      );
      const isRecommendationPage =
        history.location.pathname.includes('/recommendation');
      const isFeedbackPage = history.location.pathname.includes('/feedback');
      const isEndPage = history.location.pathname.includes('/end');

      const isInvalidNavigation =
        !isIncompleteFormPage &&
        !isRecommendationPage &&
        !isFeedbackPage &&
        !isEndPage;
      if (isInvalidNavigation) {
        history.replace({
          pathname: getFormEndPath(paramAuthCode, multi),
          search: '',
        });
      }
    }
  }, [history, formSets, paramAuthCode, multi]);

  useEffect(() => {
    if (!pathFormSetId) {
      setSelectedFormSetId(null);
      setSelectedFormId(null);
      return;
    }
    if (pathFormSetId && selectedFormSetId !== pathFormSetId) {
      setSelectedFormSetId(pathFormSetId);
    }
    if (currentFormId && selectedFormId !== currentFormId) {
      setSelectedFormId(currentFormId);
    }
  }, [
    setSelectedFormId,
    setSelectedFormSetId,
    pathFormSetId,
    selectedFormSetId,
    selectedFormId,
    currentFormId,
  ]);

  // In case landing on a page for specific form, we need to guarantee selectedFormSetId exist in store by the time children component are mounted
  // If we land on a general page without a selected form set id, then we can proceed even without selectedFormSetId
  if (
    !formSetIdIdentified ||
    _.isEmpty(formSets) ||
    storeAuthCode !== paramAuthCode
  ) {
    return <Loader />;
  }

  return <>{children}</>;
};

const ConnectedPatientFetcher = connect(
  (state: store.All) => ({
    authenticationCode: state.authenticationCode,
    formSets: state.formSets,
    preferredLanguage: state.preferredLanguage,
    selectedFormSetId: state.selectedFormSetId,
    selectedFormId: state.selectedFormId,
    coordinates: state.coordinates,
  }),
  (dispatch) => ({
    setSelectedFormSetId: (selectedFormSetId: string | null) =>
      dispatch(rootReducer.setSelectedFormSetId(selectedFormSetId)),
    setSelectedFormId: (selectedFormId: string | null) =>
      dispatch(rootReducer.setSelectedFormId(selectedFormId)),
    setFormSets: (
      formSets: api.PrevisitFormSet.PrevisitFromSetWithPatientData[],
    ) => dispatch(rootReducer.setFormSets({ formSets })),
    setMultiFormSet: (multi: boolean) =>
      dispatch(rootReducer.setMultiFormSet(multi)),
    setAuthenticationCode: (authenticationCode: string) =>
      dispatch(rootReducer.setAuthenticationCode({ authenticationCode })),
    setLoggedInPatient: (patient: api.Patient.Patient) =>
      dispatch(rootReducer.setLoggedInPatient(patient)),
    setDocuments: (
      formSetId: string,
      documents: api.PrevisitDocument.DocumentWithTemplate[],
    ) => dispatch(rootReducer.setDocuments({ formSetId, documents })),
    setFeedback: (feedback: store.PatientFeedback | null) =>
      dispatch(rootReducer.setPatientFeedback(feedback)),
    setTheme: (theme: api.PracticeTheme.ThemeResponse) =>
      dispatch(rootReducer.setTheme(theme)),
    setCoordinates: (coordinates: api.Places.Geometry | undefined) =>
      dispatch(rootReducer.setCoordinates(coordinates)),
  }),
)(withTranslation()(MakePatientFetcher));

interface FetcherProps {
  multi?: boolean;
}
/**
 * withFetchPatient is a higher-order component (HOC) that will fetch and provide a patient to the wrapped component
 * on mount. If the fetch fails for whatever reason, it will redirect the user to the login page.
 * @param Component The component to wrap
 */
export const withFetchPatient =
  <P extends {}>(
    Component: React.ComponentType<P>,
    fetcherProps?: FetcherProps,
  ): React.FunctionComponent<P> =>
  (
    props: Omit<
      MakePatientFetcherProps,
      | 'multi'
      | 'children'
      | keyof DispatchProps
      | keyof StateProps
      | keyof WithTranslation
    >,
  ) => (
    <ConnectedPatientFetcher {...fetcherProps}>
      <Component {...(props as P)} />
    </ConnectedPatientFetcher>
  );
